// LifecycleBanner.tsx — the strip above the open conversation: what the
// workspace's Pod is doing, whether the page is still live, and the
// Code/Chat toggle, Open in VS Code, Fork, the session's cost and
// Suspend/Resume.
//
// Rendered between the page chrome and the message scroller so it is
// always visible without scrolling.
import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BsShare } from 'react-icons/bs';
import { workspacesApi } from '../api';
import { getColorScheme } from '../common/theme';
import type { SessionCost } from '../api';
import OpenInVsCodeButton from './codelaunch/OpenInVsCodeButton';
import type { SessionPhase, SocketStatus } from './live/sessionLive';
import SessionCostBadge from './SessionCostBadge';

export interface LifecycleBannerProps {
  sessionPhase: SessionPhase | null;
  /** Health of the manager socket. */
  managerSocket: SocketStatus;
  /** Health of the open session's harness socket. */
  sessionSocket: SocketStatus;
  /** The workspace's Code OSS is the visible view. */
  codeOpen: boolean;
  onToggleCode: () => void;
  /** The session's workspace, which Open in VS Code opens in a new tab. */
  workspaceId: string | null;
  onCodeLaunchError: (message: string) => void;
  forking: boolean;
  onFork: () => void;
  sessionCost: SessionCost | null;
  lifecycleBusy: boolean;
  onSuspend: () => void;
  onResume: () => void;
}

const LifecycleBanner: React.FC<LifecycleBannerProps> = ({
  sessionPhase, managerSocket, sessionSocket, codeOpen, onToggleCode,
  workspaceId, onCodeLaunchError, forking, onFork, sessionCost, lifecycleBusy, onSuspend, onResume,
}) => (
  <div className="border-bottom px-3 py-2 d-flex align-items-center justify-content-between gap-2 small">
    <span className="text-muted">
      {sessionPhase === 'ready' && (
        <><span className="text-success">●</span> Workspace ready</>
      )}
      {sessionPhase === 'provisioning' && (
        <><Spinner animation="border" size="sm" className="me-2" />
          Pod provisioning…</>
      )}
      {sessionPhase === 'suspended' && (
        <><span className="text-warning">●</span> Workspace suspended</>
      )}
      {sessionPhase === null && (
        <>Connecting…</>
      )}
      {/* Say when the page has stopped being live. Everything still works —
          sends fall back to streaming over the response — but a stale list
          that looks current is worse than one that admits it. */}
      {managerSocket !== 'open' && (
        <span className="text-warning ms-2" title="Reconnecting to the manager. The session list and status may be out of date.">
          · offline
        </span>
      )}
      {sessionPhase === 'ready' && sessionSocket === 'closed' && (
        <span className="text-muted ms-2" title="Reconnecting to this session.">
          · reconnecting
        </span>
      )}
    </span>
    <div className="d-flex align-items-center gap-2">
      <Button
        size="sm"
        variant={codeOpen ? 'secondary' : 'outline-secondary'}
        onClick={onToggleCode}
        title="Open this session's workspace in Code OSS"
      >
        {codeOpen ? 'Chat' : 'Code'}
      </Button>
      {workspaceId && (
        <OpenInVsCodeButton
          workspaceId={workspaceId}
          launch={(id) => workspacesApi.codeLaunch(id, getColorScheme())}
          onError={onCodeLaunchError}
        />
      )}
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={onFork}
        disabled={forking || sessionPhase !== 'ready'}
        title="Branch this conversation into a new session in the same workspace"
        className="d-flex align-items-center gap-1"
      >
        {forking ? <Spinner animation="border" size="sm" /> : <BsShare />} Fork
      </Button>
      {/* What this conversation has cost so far. Sits with the lifecycle
          controls rather than in the composer: it describes the transcript
          above, not the turn about to be sent. */}
      <SessionCostBadge cost={sessionCost} />
      {sessionPhase === 'suspended' ? (
        <Button
          size="sm"
          variant="outline-success"
          onClick={onResume}
          disabled={lifecycleBusy}
        >
          {lifecycleBusy ? <Spinner animation="border" size="sm" /> : 'Resume'}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline-warning"
          onClick={onSuspend}
          disabled={lifecycleBusy || sessionPhase !== 'ready'}
          title="Tear down the workspace's pod, for every session in it. Files and history are kept."
        >
          {lifecycleBusy ? <Spinner animation="border" size="sm" /> : 'Suspend'}
        </Button>
      )}
    </div>
  </div>
);

export default LifecycleBanner;
